import { useEffect, useRef, useState } from "react";
import { Flag, Plus, X, AlertTriangle } from "lucide-react";
import Text from "../../components/Text";
import useCTX from "../../hooks/useCTX";
import Staff from "../../components/Staff";
import mut from "../../hooks/mut";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * Issues - Displays reported issues for a lesson and allows reporting new ones
 * @param {object} props
 * @param {object} props.cLesson - Curriculum lesson data with issuesDetail
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 3.0.0
 * @since 2026-04-14
 */
export default function Issues({ cLesson }) {
  const [report, setReport] = useState(false);
  const { primaryColor } = usePrimaryColor();
  const [description, setDescription] = useState("");
  const [issues, setIssues] = useState(cLesson.issuesDetail || []);
  const { user } = useCTX();
  const inputRef = useRef(null);

  const handleEnter = async (e) => {
    if (e.key === "Enter") {
      let newIssues = await mut("post", `/report-issue/${cLesson._id}`, {
        createdBy: user._id,
        description,
      });
      setIssues(newIssues);
      setReport(false);
      setDescription("");
    }
  };

  useEffect(() => {
    if (report && inputRef.current) {
      inputRef.current.focus();
    }
  }, [report]);

  return (
    <div className="border border-slate-200/80 bg-white rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <AlertTriangle size={15} className="text-amber-500" />
          Issues
          {issues.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-50 text-amber-600 border border-amber-200">
              {issues.length}
            </span>
          )}
        </h3>
        {!report && (
          <button
            onClick={() => setReport(true)}
            className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-[11px] font-medium rounded-[8px] bg-slate-50 text-slate-500 hover:bg-${primaryColor}-50 hover:text-${primaryColor}-800 border border-slate-200 hover:border-${primaryColor}-800/20 transition-all duration-150 cursor-pointer`}
          >
            <Plus size={12} />
            Report
          </button>
        )}
      </div>

      <div className="p-5">
        {report && (
          <div className="mb-4 flex items-start gap-2">
            <div className="flex-1">
              <Text
                ref={inputRef}
                value={description}
                setValue={setDescription}
                onKeyDown={handleEnter}
                size="small"
                fullWidth
                label="Describe the issue and press Enter..."
              />
            </div>
            <button
              onClick={() => { setReport(false); setDescription(""); }}
              className="mt-1 p-1.5 rounded-[8px] text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-150 cursor-pointer"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {issues.length === 0
          ? (
            <div className="flex flex-col items-center py-6 text-slate-300">
              <Flag size={24} className="mb-2" />
              <p className="text-xs">No issues reported</p>
            </div>
          )
          : (
            <div className="space-y-3">
              {issues.map((issue, idx) => <DrawIssue key={idx} issue={issue} />)}
            </div>
          )}
      </div>
    </div>
  );
}

/**
 * DrawIssue - Renders a single reported issue
 * @param {object} props
 * @param {object} props.issue - Issue record with createdBy, createdAt, description
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 3.0.0
 * @since 2026-04-14
 */
function DrawIssue({ issue }) {
  const dt = new Date(issue.createdAt);
  return (
    <div className="flex gap-3 p-3 bg-slate-50/80 rounded-[10px] border border-slate-100">
      <div className="p-1.5 bg-amber-50 rounded-lg border border-amber-100 flex-shrink-0 mt-0.5">
        <Flag size={12} className="text-amber-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{issue.description}</p>
        <p className="text-[10px] text-slate-400 mt-1">
          <Staff id={issue.createdBy} /> &middot; {dt.fDate()} at {dt.fTime()}
        </p>
      </div>
    </div>
  );
}
